import type { BillingDetails } from "./eventSalesShared";
import {
  getEventInvoiceAttendeeCustomFieldAnswerInputs,
  type EventInvoiceAttendee,
  type EventTicketInvoiceLine,
} from "./eventInvoiceCreateState";

type EventInvoiceDateState = {
  dueDate: string;
  invoiceDate: string;
};

type EventInvoiceFormDirtyState = EventInvoiceDateState & {
  billing: BillingDetails;
  referencePurchaseOrder: string;
  taxExempt: boolean;
};

type EventInvoiceAttendeeDirtyState = Omit<EventInvoiceAttendee, "id">;

type GetEventInvoiceAttendeeDirtyStateInput = Readonly<{
  attendee: EventInvoiceAttendee;
}>;

const getEventInvoiceAttendeeDirtyState = ({
  attendee,
}: GetEventInvoiceAttendeeDirtyStateInput): EventInvoiceAttendeeDirtyState => ({
  // Compare the values that would be saved. Empty answers and map insertion
  // order must not leave a restored form dirty or change an earlier baseline.
  companyName: attendee.companyName || "",
  customFieldAnswers: Object.fromEntries(
    getEventInvoiceAttendeeCustomFieldAnswerInputs({ attendee })
      .sort((left, right) =>
        left.fieldId < right.fieldId ? -1 : left.fieldId > right.fieldId ? 1 : 0,
      )
      .map(({ fieldId, value }) => [fieldId, value]),
  ),
  email: attendee.email,
  name: attendee.name,
  phone: attendee.phone,
  staffNotes: attendee.staffNotes || "",
});

type EventTicketInvoiceLineDirtyState = Omit<
  EventTicketInvoiceLine,
  "attendees" | "id"
> & {
  attendees: EventInvoiceAttendeeDirtyState[];
};

export type TicketInvoiceFormDirtyState = EventInvoiceFormDirtyState & {
  billingMode: "member" | "non-member";
  taxExemptionNumber: string;
  ticketLines: EventTicketInvoiceLineDirtyState[];
};

export const getTicketInvoiceFormDirtyState = ({
  billingMode = "member",
  billing,
  dueDate,
  invoiceDate,
  referencePurchaseOrder,
  taxExempt,
  taxExemptionNumber = "",
  ticketLines,
}: EventInvoiceFormDirtyState & {
  billingMode?: "member" | "non-member";
  taxExemptionNumber?: string;
  ticketLines: EventTicketInvoiceLine[];
}): TicketInvoiceFormDirtyState => ({
  billingMode,
  billing: { ...billing },
  dueDate,
  invoiceDate,
  referencePurchaseOrder,
  taxExempt,
  taxExemptionNumber,
  // Keep line and attendee order because both appear in the saved invoice.
  ticketLines: ticketLines.map((line) => ({
    attendees: line.attendees.map((attendee) =>
      getEventInvoiceAttendeeDirtyState({ attendee }),
    ),
    discountAmount: line.discountAmount,
    discountMode: line.discountMode,
    discountPercentage: line.discountPercentage,
    quantity: line.quantity,
    ticketTypeId: line.ticketTypeId,
  })),
});

export type SponsorshipInvoiceFormDirtyState = EventInvoiceFormDirtyState & {
  attendees: EventInvoiceAttendeeDirtyState[];
  packageId: string;
};

export const getSponsorshipInvoiceFormDirtyState = ({
  attendees,
  billing,
  dueDate,
  invoiceDate,
  packageId,
  referencePurchaseOrder,
  taxExempt,
}: EventInvoiceFormDirtyState & {
  attendees: EventInvoiceAttendee[];
  packageId: string;
}): SponsorshipInvoiceFormDirtyState => ({
  attendees: attendees.map((attendee) =>
    getEventInvoiceAttendeeDirtyState({ attendee }),
  ),
  billing: { ...billing },
  dueDate,
  invoiceDate,
  packageId,
  referencePurchaseOrder,
  taxExempt,
});

export const shouldRebaseEventInvoiceDateDefaults = ({
  baseline,
  current,
}: {
  baseline: EventInvoiceDateState;
  current: EventInvoiceDateState;
}): boolean =>
  current.invoiceDate === baseline.invoiceDate &&
  current.dueDate === baseline.dueDate;

export const shouldConfirmEventSelectionChange = ({
  currentEventId,
  formDirty,
  nextEventId,
}: {
  currentEventId: string;
  formDirty: boolean;
  nextEventId: string;
}): boolean =>
  Boolean(currentEventId) && formDirty && nextEventId !== currentEventId;
